import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Plus, Edit, Trash2, Loader2 } from "lucide-react";
import { DateRange } from "react-day-picker";

import { Button } from "@/shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { Badge } from "@/shared/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/ui/alert-dialog";
import { Pagination, DataTable } from "@/shared/widgets";
import { DateRangePicker } from "@/shared/widgets/DateRangePicker";

import {
  usePayments,
  useDeletePayment,
} from "../hooks";
import {
  Payment,
  PaymentStatus,
  PAYMENT_STATUS_LABELS,
} from "../utils/types";

const DEFAULT_PAGE_SIZE = 10;

const paymentStatusOptions = ["all", ...Object.values(PaymentStatus)] as const;

type StatusFilter = typeof paymentStatusOptions[number];

const formatCurrency = (value: number) =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export function PaymentsList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(
    (searchParams.get("status") as StatusFilter) ?? "all"
  );
  const [currentPage, setCurrentPage] = useState(
    Number(searchParams.get("page") ?? "1") || 1
  );
  const [pageSize, setPageSize] = useState(
    Number(searchParams.get("pageSize") ?? String(DEFAULT_PAGE_SIZE)) || DEFAULT_PAGE_SIZE
  );
  const [paymentToDelete, setPaymentToDelete] = useState<Payment | null>(null);

  // Função auxiliar para converter YYYY-MM-DD para Date
  const parseDateFromApi = (dateString?: string): Date | undefined => {
    if (!dateString) return undefined;
    try {
      // Se for formato YYYY-MM-DD, cria data no timezone local
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-').map(Number);
        return new Date(year, month - 1, day);
      }
      // Se for ISO string, usa new Date normalmente
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return undefined;
      return date;
    } catch {
      return undefined;
    }
  };

  // Inicializa ranges de data a partir dos query params
  const getInitialDateRange = (startParam?: string, endParam?: string): DateRange | undefined => {
    const from = parseDateFromApi(startParam ?? undefined);
    const to = parseDateFromApi(endParam ?? undefined);
    if (!from && !to) return undefined;
    return { from, to };
  };

  const [paymentDateRange, setPaymentDateRange] = useState<DateRange | undefined>(
    getInitialDateRange(
      searchParams.get("paymentDateStart") ?? undefined,
      searchParams.get("paymentDateEnd") ?? undefined
    )
  );
  const [referenceDateRange, setReferenceDateRange] = useState<DateRange | undefined>(
    getInitialDateRange(
      searchParams.get("referenceDateStart") ?? undefined,
      searchParams.get("referenceDateEnd") ?? undefined
    )
  );

  // Formata data para YYYY-MM-DD sem considerar timezone
  const formatDateForApi = useCallback((date?: Date): string | undefined => {
    if (!date) return undefined;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  const filters = useMemo(() => ({
    page: currentPage,
    limit: pageSize,
    status: statusFilter,
    paymentDateStart: formatDateForApi(paymentDateRange?.from),
    paymentDateEnd: formatDateForApi(paymentDateRange?.to),
    referencePaymentDateStart: formatDateForApi(referenceDateRange?.from),
    referencePaymentDateEnd: formatDateForApi(referenceDateRange?.to),
    sortBy: "paymentDate" as const,
    order: "desc" as const,
  }), [currentPage, pageSize, statusFilter, paymentDateRange, referenceDateRange, formatDateForApi]);

  const {
    payments,
    totalItems,
    totalPages,
    page,
    isLoading,
    isFetching,
    queryResult,
  } = usePayments(filters);
  
  const error = queryResult.error;

  const { deletePayment, isLoading: isDeleting } = useDeletePayment();

  // Atualiza query params quando filtros/paginação mudam
  useEffect(() => {
    const params = new URLSearchParams();
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (paymentDateRange?.from) params.set("paymentDateStart", formatDateForApi(paymentDateRange.from)!);
    if (paymentDateRange?.to) params.set("paymentDateEnd", formatDateForApi(paymentDateRange.to)!);
    if (referenceDateRange?.from) params.set("referenceDateStart", formatDateForApi(referenceDateRange.from)!);
    if (referenceDateRange?.to) params.set("referenceDateEnd", formatDateForApi(referenceDateRange.to)!);
    if (currentPage > 1) params.set("page", String(currentPage));
    if (pageSize !== DEFAULT_PAGE_SIZE) params.set("pageSize", String(pageSize));
    setSearchParams(params, { replace: true });
  }, [statusFilter, paymentDateRange, referenceDateRange, currentPage, pageSize, setSearchParams, formatDateForApi]);

  // Reseta página ao alterar filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, paymentDateRange, referenceDateRange, pageSize]);

  useEffect(() => {
    if (page && page !== currentPage) {
      setCurrentPage(page);
    }
  }, [page, currentPage]);

  const handleDelete = async () => {
    if (!paymentToDelete) return;
    try {
      await deletePayment({ id: paymentToDelete.id });
      setPaymentToDelete(null);
    } catch (error) {
      console.error("Erro ao remover pagamento:", error);
    }
  };

  const formatDateTime = (value?: string | null) => {
    if (!value) return "—";
    try {
      return format(new Date(value), "dd/MM/yyyy HH:mm", { locale: ptBR });
    } catch {
      return value;
    }
  };

  const formatDate = (value?: string | null) => {
    if (!value) return "—";
    try {
      return format(new Date(value), "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return value;
    }
  };

  const statusBadgeVariant = (status: PaymentStatus) => {
    switch (status) {
      case PaymentStatus.PAID:
        return "default" as const;
      case PaymentStatus.SCHEDULED:
        return "secondary" as const;
      case PaymentStatus.ERROR:
        return "destructive" as const;
      default:
        return "outline" as const;
    }
  };

  const filtersApplied = Boolean(
    statusFilter !== "all" ||
    paymentDateRange?.from ||
    referenceDateRange?.from,
  );

  const handleClearFilters = () => {
    setStatusFilter("all");
    setPaymentDateRange(undefined);
    setReferenceDateRange(undefined);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Pagamentos</h1>
          <p className="text-muted-foreground">
            Cadastre e acompanhe os pagamentos realizados para os creators.
          </p>
        </div>
        <Button onClick={() => navigate("/payments/new")}>
          <Plus className="mr-2 h-4 w-4" />
          Novo pagamento
        </Button>
      </div>

      {/* Filtros */}
      <Card className="bg-white/[0.03] border-white/[0.06]">
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
          <CardDescription>Filtre por status, período de pagamento ou período de referência</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <Select
              value={statusFilter}
              onValueChange={(value) => setStatusFilter(value as StatusFilter)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                {Object.values(PaymentStatus).map((status) => (
                  <SelectItem key={status} value={status}>
                    {PAYMENT_STATUS_LABELS[status]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <DateRangePicker
              dateRange={paymentDateRange}
              setDateRange={(range) => setPaymentDateRange(range)}
              placeholder="Período de pagamento"
            />
            <DateRangePicker
              dateRange={referenceDateRange}
              setDateRange={(range) => setReferenceDateRange(range)}
              placeholder="Período de referência"
            />
          </div>

          {filtersApplied && (
          <div className="flex justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
            >
              Limpar filtros
            </Button>
          </div>
          )}
        </CardContent>
      </Card>

      {/* Tabela */}
      <DataTable
        data={payments}
        isLoading={isLoading}
        error={error}
        emptyTitle="Nenhum pagamento encontrado"
        emptyDescription={(hasFilters) =>
          hasFilters
            ? "Tente ajustar os filtros"
            : "Comece criando um novo pagamento"
        }
        emptyActionLabel="Novo pagamento"
        emptyActionOnClick={() => navigate("/payments/new")}
        hasFilters={filtersApplied}
        errorMessage={`Erro ao carregar pagamentos: ${error?.message}`}
        columns={[
          {
            header: "Creator",
            cell: (payment) => (
                        <div className="flex flex-col">
                          <span className="font-medium">{payment.creator?.name ?? "—"}</span>
                          <span className="text-xs text-muted-foreground">{payment.id}</span>
                        </div>
            ),
          },
          {
            header: "Conta bancária",
            cell: (payment) =>
              payment.bankAccount ? (
                          <div className="flex flex-col text-sm">
                            <span className="font-medium">{payment.bankAccount.bank}</span>
                            <span className="text-xs text-muted-foreground">
                              Ag. {payment.bankAccount.branch} • Conta {payment.bankAccount.account}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
              ),
          },
          {
            header: "Status",
            cell: (payment) => (
                        <Badge variant={statusBadgeVariant(payment.status)}>
                          {PAYMENT_STATUS_LABELS[payment.status] ?? payment.status}
                        </Badge>
            ),
          },
          {
            header: "Valor",
            cell: (payment) => (
              <span className="text-right font-medium">{formatCurrency(payment.value)}</span>
            ),
            className: "text-right",
          },
          {
            header: "Data pagamento",
            cell: (payment) => formatDate(payment.paymentDate),
          },
          {
            header: "Referência",
            cell: (payment) => formatDate(payment.referencePaymentDate),
          },
          {
            header: "Atualizado em",
            cell: (payment) => formatDateTime(payment.lastUpdatedAt ?? payment.createdAt),
          },
          {
            header: "Ações",
            cell: (payment) => (
              <div className="flex justify-end gap-2">
                        <Button
                  variant="ghost"
                          size="icon"
                          onClick={() => navigate(`/payments/${payment.id}`)}
                  title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                  variant="ghost"
                          size="icon"
                          onClick={() => setPaymentToDelete(payment)}
                  title="Excluir"
                        >
                  <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
            </div>
            ),
            className: "text-right",
          },
        ]}
      />

      {/* Pagination */}
      {!isLoading && !error && totalPages > 0 && (
          <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
            totalItems={totalItems}
          setCurrentPage={setCurrentPage}
            pageSize={pageSize}
          showPageSizeOptions
            pageSizeOptions={[10, 20, 50]}
          onPageSizeChange={(size) => {
            setCurrentPage(1);
            setPageSize(size);
          }}
          showTotalItems
          syncWithQueryParams={false}
        />
      )}

      <AlertDialog open={Boolean(paymentToDelete)} onOpenChange={(open) => !open && setPaymentToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover pagamento?</AlertDialogTitle>
            <AlertDialogDescription>
              Essa ação é irreversível. O pagamento será removido definitivamente
              do histórico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Removendo...
                </>
              ) : (
                "Remover"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
